import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { trackEvent } from 'helpers/Analytics';
import { loadActive, recordAction } from 'redux/modules/announcements';
import { isInternalUrl, trackingData } from './announcementContent';
import AnnouncementModalView from './AnnouncementModalView';

// Never interrupt a task: no modals while messaging, applying, posting,
// signing up or paying
const BUSY_PATHS = [
  '/messages',
  '/post',
  '/projects',
  '/onboarding',
  '/signup',
  '/signin',
  '/pricing',
  '/checkout',
];

const isBusy = (pathname) =>
  BUSY_PATHS.some((path) => pathname.indexOf(path) === 0);

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Keeps Tab inside the dialog, closes on Escape and restores focus after. */
function useDialogFocus(rootRef, open, onClose) {
  // The listener is added once per opening, so it reads the latest onClose
  // (and with it the current step) through a ref
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || !rootRef.current) {
      return undefined;
    }

    const previouslyFocused = document.activeElement;
    const focusable = () =>
      Array.from(rootRef.current.querySelectorAll(FOCUSABLE));
    const first = focusable()[0];

    if (first) {
      first.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const items = focusable();

      if (items.length === 0) {
        return;
      }

      const firstItem = items[0];
      const lastItem = items[items.length - 1];

      if (event.shiftKey && document.activeElement === firstItem) {
        event.preventDefault();
        lastItem.focus();
      } else if (!event.shiftKey && document.activeElement === lastItem) {
        event.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);

      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [open]);
}

/**
 * The Tier 1 launch modal. Shown once, at a natural pause, on the first page
 * load after launch. Closing it hands over to the announcement banner.
 */
const AnnouncementModal = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const announcement = useSelector((state) => state.announcements.active.modal);
  const [step, setStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const rootRef = useRef(null);

  const modal = announcement && announcement.modal;
  const steps = (modal && modal.steps) || [];
  const open = !!announcement && steps.length > 0 && !dismissed;
  const visible = open && !isBusy(pathname);

  useEffect(() => {
    if (visible) {
      trackEvent(
        'announcement-modal-shown',
        trackingData(announcement, 'modal')
      );
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      trackEvent('announcement-modal-step-viewed', {
        ...trackingData(announcement, 'modal'),
        step: step + 1,
      });
    }
  }, [visible, step]);

  const finish = (stateAction) => {
    setDismissed(true);
    dispatch(recordAction(announcement.id, 'modal-seen')).then(() =>
      // The banner follows the modal once it's been seen
      dispatch(loadActive())
    );

    if (stateAction) {
      dispatch(recordAction(announcement.id, stateAction));
    }
  };

  const close = () => {
    trackEvent('announcement-modal-closed', {
      ...trackingData(announcement, 'modal'),
      step: step + 1,
    });
    finish();
  };

  useDialogFocus(rootRef, visible, close);

  if (!visible) {
    return null;
  }

  // The view only calls this once any required box is ticked
  const handleCta = () => {
    if (modal.requires_ack) {
      trackEvent(
        'announcement-modal-acknowledged',
        trackingData(announcement, 'modal')
      );
      dispatch(recordAction(announcement.id, 'acknowledge'));
    }

    trackEvent(
      'announcement-modal-cta-clicked',
      trackingData(announcement, 'modal')
    );
    finish('cta-clicked');

    if (isInternalUrl(announcement.cta_url)) {
      history.push(announcement.cta_url);
    } else {
      window.location.href = announcement.cta_url;
    }
  };

  const handleGuide = () => {
    trackEvent(
      'announcement-modal-guide-clicked',
      trackingData(announcement, 'modal')
    );
    finish();
  };

  return (
    <AnnouncementModalView
      announcement={announcement}
      step={step}
      onStepChange={setStep}
      onClose={close}
      onCta={handleCta}
      onGuide={handleGuide}
      dialogRef={rootRef}
    />
  );
};

export default AnnouncementModal;
